import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { CheckCircle, Close } from "@mui/icons-material";
import apiConfig from '../config/api.js';

export default function DeveloperButton({ store }) {
  const isLang = useSelector((state) => state.authReducer.lang);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState([]); // Store categories
  const [packages, setPackages] = useState([]); // Store packages
  const [category, setCategory] = useState('');
  const [pkgid, setPackageType] = useState('');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [jsonFile, setJsonFile] = useState(null); // Store selected JSON file
  const [image, setImage] = useState(null); // Store selected image
  const [loading, setLoading] = useState(false); // Loading state
  const [showForm, setShowForm] = useState(false); // Show form after clicking upload
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar state for errors
  const developeruid = useSelector((state) => state.authReducer.developeruid);

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${apiConfig.API_URL}/aapi/getcategories.php`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error.message);
    }
  };

  // Fetch Packages
  const fetchPackages = async () => {
    try {
      const response = await fetch(`${apiConfig.API_URL}/aapi/getPackages.php`);
      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }
      const data = await response.json();
      setPackages(data);
    } catch (error) {
      console.error('Error fetching packages:', error.message);
    }
  };

  // Fetch categories and packages on component mount
  useEffect(() => {
    fetchCategories();
    fetchPackages();
  }, []);

  // When category changes, set amount based on selected category
  const handleCategoryChange = (e) => {
    const selectedCategoryId = e.target.value;
    setCategory(selectedCategoryId);

    // Find the selected category's amount
    const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
    if (selectedCategory) {
      setAmount(selectedCategory.amount || ''); // Assuming 'amount' is a property of category
    }
  };

  const handleDownloadAsDeveloper = async () => {
    try {
      const finalFileName = "developer_download"; // Fixed file name or you can use logic to create dynamic name

      // --- JSON DOWNLOAD ---
      const jsonData = store.toJSON();
      const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      setJsonFile(jsonBlob); // Save JSON blob in state

      const jsonLink = document.createElement('a');
      jsonLink.href = URL.createObjectURL(jsonBlob);
      jsonLink.download = finalFileName + ".json";
      document.body.appendChild(jsonLink);
      jsonLink.click();
      document.body.removeChild(jsonLink);

      // --- PNG DOWNLOAD ---
      const canvasDataURL = await store.toDataURL({
        pixelRatio: 1,
        mimeType: "image/png",
      });
      const pngBlob = await fetch(canvasDataURL).then((res) => res.blob());
      setImage(pngBlob); // Save PNG blob in state

      const pngLink = document.createElement('a');
      pngLink.href = URL.createObjectURL(pngBlob);
      pngLink.download = finalFileName + ".png";
      document.body.appendChild(pngLink);
      pngLink.click();
      document.body.removeChild(pngLink);
    } catch (error) {
      console.error("Error downloading JSON or PNG:", error);
      alert("Failed to download JSON or PNG. Please try again.");
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!category || !pkgid || !title || !jsonFile || !image || !amount) {
      console.error('Please fill in all required fields');
      setSnackbarOpen(true);
      return;
    }

    console.log("Input data:", {
      pkgid,
      title,
      templetepath: jsonFile,
      imagepath: image,
      category,
      amount,
    });

    try {
      setLoading(true); // Start loading spinner when the upload begins
      const formDataToSend = new FormData();
      formDataToSend.append('pkgid', pkgid);
      formDataToSend.append('title', title);
      formDataToSend.append('templetepath', jsonFile);
      formDataToSend.append('imagepath', image);
      formDataToSend.append('category', category);
      formDataToSend.append('amount', amount);
      formDataToSend.append('uploaded_by', developeruid);
      formDataToSend.append('comments',"None");

      const response = await fetch(`${apiConfig.API_URL}/aapi/developer_api/uploadtemplete.php`, {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        console.log('Template data successfully sent to the API', response.status);
        setShowForm(false);
        setShowSuccessPopup(true);
      } else {
        console.error('Failed to send data to the API. Status:', response.status);
      }
    } catch (error) {
      console.error('Error saving template:', error.message);
    } finally {
      setLoading(false); // Stop loading spinner after the upload completes
    }
  };

  const handleOpen = async (e) => {
    try {
      const finalFileName = "developer_download"; // Fixed file name or you can use logic to create dynamic name

      // --- JSON PREPARATION (No Download) ---
      const jsonData = store.toJSON();
      const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      setJsonFile(jsonBlob); // Save JSON blob in state (no download)

      // --- PNG PREPARATION (No Download) ---
      const canvasDataURL = await store.toDataURL({
        pixelRatio: 1,
        mimeType: "image/png",
      });
      const pngBlob = await fetch(canvasDataURL).then((res) => res.blob());
      setImage(pngBlob); // Save PNG blob in state (no download)

      // Show form after the preparation
      setShowForm(true);
    } catch (error) {
      console.error('Error preparing template:', error.message);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} color="inherit" />
        </Box>
      )}

      {/* Download Button */}
      <button
        onClick={handleDownloadAsDeveloper}
        className="bg-green-500 p-2 px-4 text-white font-semibold text-md mr-3 hover:bg-green-700"
        style={{
          fontSize: "15px",
          borderRadius: "10px",
          color: "white",
        }}
      >
        Download
      </button>

      {/* Upload Button */}
      <button
        onClick={handleOpen}
        className="bg-purple-500 p-2 px-4 text-white font-semibold text-md mr-3 hover:bg-purple-700"
        style={{
          fontSize: "15px",
          borderRadius: "10px",
          color: "white",
        }}
      >
        Upload
      </button>

      {/* Form Dialog */}
      <Dialog open={showForm} onClose={() => setShowForm(false)}>
  <Box sx={{ p: 2 }} dir={isLang === 'arabic' ? 'rtl' : 'ltr'}>
    <DialogTitle>
      <Typography variant="h6">
        {isLang === 'arabic' ? 'تحميل القالب' : 'Upload Template'}
      </Typography>
    </DialogTitle>
    <DialogContent>
      <form onSubmit={handleFormSubmit}>
        {/* Category Select */}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>
            {isLang === 'arabic' ? 'الفئة' : 'Category'}
          </InputLabel>
          <Select
            value={category || ""}
            onChange={handleCategoryChange}
            required
          >
            {categories.length > 0 ? (
              categories.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.title}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">
                <em>
                  {isLang === 'arabic'
                    ? 'جارٍ تحميل الفئات...'
                    : 'Loading Categories...'}
                </em>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Packages Select */}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>
            {isLang === 'arabic' ? 'الحزم' : 'Packages'}
          </InputLabel>
          <Select
            value={pkgid || ""}
            onChange={(e) => setPackageType(e.target.value)}
            required
          >
            {packages.length > 0 ? (
              packages.map((packageItem) => (
                <MenuItem key={packageItem.id} value={packageItem.id}>
                  {packageItem.title}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">
                <em>
                  {isLang === 'arabic'
                    ? 'جارٍ تحميل الحزم...'
                    : 'Loading Packages...'}
                </em>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Title Input */}
        <TextField
          label={isLang === 'arabic' ? 'العنوان' : 'Title'}
          fullWidth
          sx={{ mt: 2 }}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        {/* Amount Input */}
        <TextField
          label={isLang === 'arabic' ? 'المبلغ' : 'Amount'}
          fullWidth
          sx={{ mt: 2 }}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          disabled
        />

        {/* JSON File Input */}
        <div style={{ marginTop: '20px', textAlign: isLang === 'arabic' ? 'right' : 'left' }}>
          <label>
            {isLang === 'arabic' ? 'ملف JSON المرفوع:' : 'Uploaded JSON File:'}
          </label>
          <Typography>
            {jsonFile
              ? isLang === 'arabic'
                ? 'ملف JSON جاهز'
                : 'JSON file ready'
              : isLang === 'arabic'
              ? 'لم يتم تنزيل ملف JSON بعد'
              : 'No JSON file downloaded yet'}
          </Typography>
        </div>

        {/* Image File Input */}
        <div style={{ marginTop: '20px', textAlign: isLang === 'arabic' ? 'right' : 'left' }}>
          <label>
            {isLang === 'arabic' ? 'الصورة المرفوعة:' : 'Uploaded Image:'}
          </label>
          <Typography>
            {image
              ? isLang === 'arabic'
                ? 'ملف الصورة جاهز'
                : 'Image file ready'
              : isLang === 'arabic'
              ? 'لم يتم تنزيل صورة بعد'
              : 'No image downloaded yet'}
          </Typography>
        </div>

        {/* Submit Button */}
        <DialogActions>
          <Button
            variant="contained"
            type="submit"
            sx={{ mt: 2 }}
          >
            {isLang === 'arabic' ? 'تحميل' : 'Upload'}
          </Button>
        </DialogActions>
      </form>
    </DialogContent>
  </Box>
</Dialog>


      <Dialog
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setShowSuccessPopup(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
          <Typography variant="h6">Success</Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center">
            <CheckCircle color="success" sx={{ fontSize: 40, mr: 2 }} />
            <Typography>Your template has been submitted to the admin!</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
